// Customer web portal API. Uses its own axios instance so the admin interceptors do not apply.
import axios, { AxiosError } from 'axios';
import { LEAD_API_PREFIX, LEAD_SERVICE_HOST } from '../constants/RestConstants';
import { CUSTOMER_LOGIN } from '../constants/URIConstants';
import { translate } from '../../i18n/store';

const TOKEN_COOKIE = 'auth_token';
const ROLE_COOKIE = 'user_role';
const NAME_KEY = 'whatzneu.customer.name';
const SESSION_MAX_AGE_MS = 60 * 24 * 60 * 60 * 1000;

export interface CustomerSession {
  token: string;
  expiresAt: Date;
}

function readCookie(name: string): string | undefined {
  const match = document.cookie
    .split(';')
    .map(part => part.trim())
    .find(part => part.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.slice(name.length + 1)) : undefined;
}

function writeCookie(name: string, value: string, expires: Date) {
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires.toUTCString()}; path=/; SameSite=Lax${secure}`;
}

function eraseCookie(name: string) {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
}

function tokenExpiry(token: string): Date | undefined {
  try {
    const payload = token.split('.')[1];
    if (!payload) return undefined;
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(payload.length / 4) * 4, '='));
    const exp = (JSON.parse(json) as { exp?: unknown }).exp;
    return typeof exp === 'number' ? new Date(exp * 1000) : undefined;
  } catch {
    return undefined;
  }
}

/** Keeps the sign-in until the token expires, capped at 60 days, instead of a browser-session cookie. */
export function saveCustomerSession(token: string, customerName?: string) {
  const cap = new Date(Date.now() + SESSION_MAX_AGE_MS);
  const exp = tokenExpiry(token);
  const expires = exp && exp < cap ? exp : cap;
  writeCookie(TOKEN_COOKIE, token, expires);
  writeCookie(ROLE_COOKIE, 'CUSTOMER', expires);
  if (customerName) localStorage.setItem(NAME_KEY, customerName);
}

export function currentCustomerSession(): CustomerSession | null {
  const token = readCookie(TOKEN_COOKIE);
  if (!token || readCookie(ROLE_COOKIE) !== 'CUSTOMER') return null;
  const expiresAt = tokenExpiry(token);
  if (expiresAt && expiresAt.getTime() <= Date.now()) return null;
  return { token, expiresAt: expiresAt ?? new Date(Date.now() + SESSION_MAX_AGE_MS) };
}

export function storedCustomerName(): string | undefined {
  return localStorage.getItem(NAME_KEY) ?? undefined;
}

export function clearCustomerSession() {
  eraseCookie(TOKEN_COOKIE);
  eraseCookie(ROLE_COOKIE);
  localStorage.removeItem(NAME_KEY);
}

const client = axios.create({
  baseURL: `${LEAD_SERVICE_HOST}${LEAD_API_PREFIX}/customer`,
  timeout: 20000,
  headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
});

client.interceptors.request.use(config => {
  const token = readCookie(TOKEN_COOKIE);
  if (token) config.headers.set('Authorization', `Bearer ${token}`);
  return config;
});

client.interceptors.response.use(
  response => response,
  (error: AxiosError) => {
    const url = error.config?.url ?? '';
    if (error.response?.status === 401 && !url.startsWith('/login')) {
      clearCustomerSession();
      window.location.assign(`${CUSTOMER_LOGIN}?expired=1`);
    }
    return Promise.reject(error);
  },
);

/** Customer-facing wording for a failed request. Never echoes stack traces or tokens. */
export function portalErrorMessage(error: unknown, fallback = translate('common.errors.generic')): string {
  if (axios.isAxiosError(error)) {
    if (!error.response) return translate(error.code === 'ECONNABORTED' ? 'common.errors.timeout' : 'common.errors.offline');
    const body = error.response.data as { message?: unknown } | undefined;
    const message = typeof body?.message === 'string' ? body.message.trim() : '';
    if (message && message.length <= 180 && !/exception|sql|stack|token|bearer|axios/i.test(message)) return message;
    if (error.response.status === 401 || error.response.status === 403) return translate('common.errors.sessionExpired');
    if (error.response.status === 400 || error.response.status === 422) return translate('common.errors.checkDetails');
  }
  return fallback;
}

type Envelope<T> = { data?: T; message?: string; statusCode?: number };
type ListPayload<T> = { result?: T[]; totalCount?: number; totalActiveAmount?: number };

function unwrap<T>(body: Envelope<T> | T): T {
  if (body && typeof body === 'object' && 'data' in (body as object)) return (body as Envelope<T>).data as T;
  return body as T;
}

export type ApiDate = string | number[] | null | undefined;

export interface CustomerProfile {
  id: string;
  name?: string;
  email?: string;
  contactNumber?: string;
  address?: string;
  city?: string;
  state?: string;
  pincode?: string;
}

export interface CustomerDebit {
  id: string;
  type?: string;
  amount?: number;
  outstanding?: number;
  interestRate?: number;
  durationType?: string;
  startDate?: ApiDate;
  endDate?: ApiDate;
  emiAmount?: number;
  paymentDate?: number;
  status?: string;
  closedAt?: ApiDate;
  lastInterestCollectedDate?: ApiDate;
}

export interface CustomerPayment {
  id: string;
  debitId?: string;
  paymentType?: string;
  amount?: number;
  note?: string;
  paymentReceivedBy?: string;
  status?: string;
  createdDate?: ApiDate;
}

export interface CustomerDeposit {
  id: string;
  amount?: number;
  currency?: string;
  depositDate?: ApiDate;
  maturityDate?: ApiDate;
  status?: string;
  referenceNumber?: string;
  source?: string;
}

export interface CustomerProduct {
  id: string;
  name: string;
  description?: string;
  category?: string;
  price?: number;
  currency?: string;
  imageUrl?: string;
  subscriptionLink?: string;
  features?: string;
  tags?: string;
  status?: string;
  isPublished?: boolean;
}

export interface CustomerInterestRequest {
  id: string;
  productId?: string;
  message?: string;
  status?: string;
  createdDate?: ApiDate;
}

export interface CustomerNotification {
  id: string;
  type?: string;
  title?: string;
  body?: string;
  read: boolean;
  createdDate?: ApiDate;
}

export interface CustomerFundRequest {
  id: string;
  requestedAmount?: number;
  minimumAmount?: number;
  monthlyInterestRate?: number;
  purpose?: string;
  status?: string;
  adminNote?: string;
  debitId?: string;
  createdDate?: ApiDate;
}

export interface CustomerReferral {
  id: string;
  prospectName?: string;
  prospectEmail?: string;
  prospectPhone?: string;
  amountRequired?: number;
  status?: string;
  adminNote?: string;
  createdDate?: ApiDate;
}

export interface CreateFundRequestInput {
  requestedAmount: number;
  minimumAmount: number;
  monthlyInterestRate: number;
  purpose: string;
}

export interface CreateReferralInput {
  prospectName: string;
  prospectEmail: string;
  prospectPhone: string;
  amountRequired: number;
}

export interface Page<T> {
  items: T[];
  totalCount: number;
}

async function list<T>(path: string, params?: Record<string, unknown>): Promise<ListPayload<T>> {
  const response = await client.get<Envelope<ListPayload<T>>>(path, { params });
  return unwrap(response.data) ?? {};
}

async function page<T>(path: string, pageNumber: number, size: number, params: Record<string, unknown> = {}): Promise<Page<T>> {
  const payload = await list<T>(path, { page: pageNumber, size, ...params });
  const items = Array.isArray(payload.result) ? payload.result : [];
  return { items, totalCount: typeof payload.totalCount === 'number' ? payload.totalCount : items.length };
}

async function every<T>(path: string, params: Record<string, unknown> = {}): Promise<T[]> {
  const size = 100;
  const items: T[] = [];
  for (let pageNumber = 0; pageNumber < 20; pageNumber += 1) {
    const next = await page<T>(path, pageNumber, size, params);
    items.push(...next.items);
    if (next.items.length === 0 || items.length >= next.totalCount) break;
  }
  return items;
}

export async function sendLoginCode(email: string): Promise<void> {
  await client.post('/login/send-otp', { email: email.trim() });
}

export async function verifyLoginCode(email: string, otp: string): Promise<{ token: string; customerName?: string }> {
  const response = await client.post<Envelope<{ token?: string; customerName?: string }>>('/login/verify-otp', {
    email: email.trim(),
    otp: otp.trim(),
  });
  const payload = unwrap(response.data);
  if (!payload?.token) throw new Error('Please check the code and try again.');
  return { token: payload.token, customerName: payload.customerName };
}

export async function fetchProfile(): Promise<CustomerProfile> {
  const response = await client.get<Envelope<CustomerProfile>>('/profile');
  return unwrap(response.data);
}

export async function fetchOutstanding(): Promise<number | undefined> {
  const response = await client.get<Envelope<{ totalOutstanding?: number }>>('/outstanding');
  const value = unwrap(response.data)?.totalOutstanding;
  return typeof value === 'number' ? value : undefined;
}

export function fetchActiveDebits(): Promise<CustomerDebit[]> {
  return page<CustomerDebit>('/debits', 0, 50, { status: 'ACTIVE' }).then(result => result.items);
}

export function fetchAllDebits(): Promise<CustomerDebit[]> {
  return every<CustomerDebit>('/debits', { status: 'ALL' });
}

export function fetchPayments(pageNumber: number, size: number): Promise<Page<CustomerPayment>> {
  return page<CustomerPayment>('/payments', pageNumber, size);
}

export async function fetchDebitPayments(debitId: string): Promise<CustomerPayment[]> {
  const all = await every<CustomerPayment>('/payments');
  return all.filter(payment => payment.debitId === debitId);
}

export async function fetchDeposits(pageNumber: number, size: number): Promise<Page<CustomerDeposit> & { totalActiveAmount?: number }> {
  const payload = await list<CustomerDeposit>('/deposits', { page: pageNumber, size });
  const items = Array.isArray(payload.result) ? payload.result : [];
  return {
    items,
    totalCount: typeof payload.totalCount === 'number' ? payload.totalCount : items.length,
    totalActiveAmount: typeof payload.totalActiveAmount === 'number' ? payload.totalActiveAmount : undefined,
  };
}

export function fetchProducts(category?: string): Promise<CustomerProduct[]> {
  return page<CustomerProduct>('/products', 0, 100, category ? { category } : {}).then(result => result.items);
}

export async function submitProductInterest(productId: string, message: string): Promise<void> {
  await client.post(`/products/${encodeURIComponent(productId)}/interest`, { productId, message: message.trim() });
}

export async function fetchInterestRequests(): Promise<CustomerInterestRequest[]> {
  const payload = await list<CustomerInterestRequest>('/interest-requests');
  return Array.isArray(payload.result) ? payload.result : [];
}

export async function fetchNotifications(): Promise<CustomerNotification[]> {
  const payload = await list<CustomerNotification>('/notifications');
  return Array.isArray(payload.result) ? payload.result : [];
}

export async function markNotificationRead(id: string): Promise<void> {
  await client.put(`/notifications/${encodeURIComponent(id)}/read`);
}

export async function markAllNotificationsRead(): Promise<void> {
  await client.put('/notifications/read-all');
}

export async function fetchFundRequests(): Promise<CustomerFundRequest[]> {
  const payload = await list<CustomerFundRequest>('/fund-requests');
  return Array.isArray(payload.result) ? payload.result : [];
}

export async function submitFundRequest(input: CreateFundRequestInput): Promise<void> {
  await client.post('/fund-requests', { ...input, purpose: input.purpose.trim() });
}

export async function fetchReferrals(): Promise<CustomerReferral[]> {
  const payload = await list<CustomerReferral>('/referrals');
  return Array.isArray(payload.result) ? payload.result : [];
}

export async function submitReferral(input: CreateReferralInput): Promise<void> {
  await client.post('/referrals', {
    prospectName: input.prospectName.trim(),
    prospectEmail: input.prospectEmail.trim(),
    prospectPhone: input.prospectPhone,
    amountRequired: input.amountRequired,
  });
}
